import { redirect } from "@tanstack/react-router"

import { resolveSession } from "@/lib/auth/session"
import type { RouterContext } from "@/main"
import type { Customer } from "@/lib/types"

/**
 * Search-param schema for the `?from=` bounce-back used by the auth
 * routes. Only internal paths are kept — anything not starting with `/`
 * is dropped, so a crafted `?from=https://evil.example/...` can't
 * redirect a freshly-logged-in user off-site.
 */
export type AuthSearch = {
  from?: string
}

export function validateAuthSearch(raw: Record<string, unknown>): AuthSearch {
  const from = raw.from
  if (typeof from === "string" && from.startsWith("/")) {
    return { from }
  }
  return {}
}

/**
 * `beforeLoad` guard for authenticated routes. Bounces to `/login`
 * (remembering where we came from) when there's no valid session, and
 * otherwise returns `{ user }` into the route context.
 */
export async function requireAuth({
  context,
  location,
}: {
  context: RouterContext
  location: { href: string }
}): Promise<{ user: Customer }> {
  const user = await resolveSession(context.queryClient)
  if (!user) {
    throw redirect({ to: "/login", search: { from: location.href } })
  }
  return { user }
}

/**
 * `beforeLoad` for the login route: if already authenticated, skip the
 * form and go to `?from=` (or `/dashboard`).
 */
export async function redirectIfAuthed({
  context,
  search,
}: {
  context: RouterContext
  search: AuthSearch
}) {
  const user = await resolveSession(context.queryClient)
  if (user) {
    // `from` is a validated internal path; cast past the typed route union.
    throw redirect({ to: (search.from ?? "/home") as never })
  }
}
